// @cursor:state-management
// @cursor:storage
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Settings {
  platforms: {
    twitter: boolean;
    bluesky: boolean;
    farcaster: boolean;
  };
  theme: 'light' | 'dark';
  clarityExamples: string;
  showHiddenPosts: boolean;
}

interface SettingsContextType {
  settings: Settings | null;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  platforms: {
    twitter: true,
    bluesky: true,
    farcaster: true
  },
  theme: 'light', // Will be overridden on mount based on system preference
  clarityExamples: '',
  showHiddenPosts: false,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    // Load settings from localStorage on mount
    const savedSettings = localStorage.getItem('settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Ensure theme exists (for backwards compatibility)
        if (!parsed.theme) {
          // Initialize based on system preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          parsed.theme = prefersDark ? 'dark' : 'light';
        }
        if (!parsed.clarityExamples) {
          parsed.clarityExamples = '';
        }
        if (typeof parsed.showHiddenPosts !== 'boolean') {
          parsed.showHiddenPosts = false;
        }
        setSettings(parsed);
        // Apply theme immediately
        applyTheme(parsed.theme);
      } catch (error) {
        console.error('Error loading settings:', error);
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const initialTheme = prefersDark ? 'dark' : 'light';
        setSettings({ ...defaultSettings, theme: initialTheme });
        applyTheme(initialTheme);
      }
    } else {
      // Initialize based on system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = prefersDark ? 'dark' : 'light';
      setSettings({ ...defaultSettings, theme: initialTheme });
      applyTheme(initialTheme);
    }
  }, []);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        ...newSettings,
        platforms: {
          ...prev?.platforms,
          ...newSettings.platforms
        }
      } as Settings;
      localStorage.setItem('settings', JSON.stringify(updated));
      
      // Apply theme immediately if it changed
      if (newSettings.theme !== undefined) {
        applyTheme(updated.theme);
      }
      
      return updated;
    });
  };

  // Apply theme on mount and when settings change
  useEffect(() => {
    if (settings?.theme) {
      applyTheme(settings.theme);
    }
  }, [settings?.theme]);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

function applyTheme(theme: 'light' | 'dark') {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  
  // Update theme-color meta tag dynamically for in-app theme toggle
  setThemeColor(theme === 'dark' ? '#000000' : '#ffffff');
}

function setThemeColor(color: string) {
  // Find or create a meta tag without media query for dynamic updates
  // This will override the media query meta tags when user manually toggles theme
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  const isNew = !meta;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
    console.log('[Theme] Created new theme-color meta tag');
  }
  const oldColor = meta.content;
  meta.content = color;
  console.log(`[Theme] ${isNew ? 'Created' : 'Updated'} theme-color meta tag:`, {
    oldColor: oldColor || '(none)',
    newColor: color,
    element: meta,
    allThemeColorTags: Array.from(document.querySelectorAll('meta[name="theme-color"]')).map(m => ({
      content: (m as HTMLMetaElement).content,
      media: (m as HTMLMetaElement).media || '(none)'
    }))
  });
} 
